import { useState } from 'react';
import { complianceApi, type ChallanPreview } from '@/api/compliance.api';
import { formatINR } from '@/utils/format';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatDate(d: string | Date | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface Props {
  /** TDS = vendor deduction (194-series), TCS = collection from buyers (206C) */
  type?: 'TDS' | 'TCS';
  onClose: () => void;
  onGenerated: () => void;
}

/**
 * Two-step TDS/TCS deposit flow (same ITNS 281 form, different section):
 *  1. Generate Challan Details — pick a month, preview the ITNS 281 section-wise
 *     breakup + interest (read-only, no DB write).
 *  2. Mark as Deposited — after actually paying at the IT portal/bank, record the
 *     real challan number/BSR code/deposit date here — this is the only step that writes.
 */
export default function ChallanDepositFlow({ type = 'TDS', onClose, onGenerated }: Props) {
  const toast = useToast();
  const { user } = useAuth();
  const now = new Date();
  const [step, setStep] = useState<'select' | 'preview' | 'deposit'>('select');
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth()); // default: previous month
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<ChallanPreview | null>(null);

  const [challanNumber, setChallanNumber] = useState('');
  const [bsrCode, setBsrCode] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankName, setBankName] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const typeLabel = type === 'TCS' ? 'TCS' : 'TDS';

  const handleGeneratePreview = async () => {
    setLoading(true);
    try {
      const res = await complianceApi.getChallanPreview(month, year, type);
      if (res?.success && res.data) {
        if (res.data.entryIds.length === 0) {
          toast.info(`No un-deposited ${type} entries found for ${MONTH_NAMES[month - 1]} ${year}`);
          return;
        }
        setPreview(res.data);
        setStep('preview');
      } else {
        toast.error(res?.message || 'Failed to generate challan preview');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate challan preview');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!preview) return;
    const lines = [
      `CHALLAN ITNS 281 — ${MONTH_NAMES[preview.periodMonth - 1]} ${preview.periodYear}`,
      `Company TAN: ${preview.companyTan || '—'}`,
      `Company PAN: ${preview.companyPan || '—'}`,
      `Assessment Year: ${preview.assessmentYear}`,
      '',
      'Section-wise breakup:',
      ...preview.sectionWise.map((s) => `  ${s.section}: Taxable ${formatINR(s.totalTaxable)}, TDS ${formatINR(s.totalTDS)}`),
      '',
      `${typeLabel} Amount: ${formatINR(preview.totalTDS)}`,
      preview.interest > 0 ? `Interest (Late): ${formatINR(preview.interest)} (1.5% × ${preview.monthsLate} month${preview.monthsLate === 1 ? '' : 's'})` : '',
      `Total Payable: ${formatINR(preview.totalPayable)}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(lines);
    toast.success('Challan details copied to clipboard');
  };

  const handleConfirmDeposit = async () => {
    if (!preview) return;
    if (!challanNumber.trim()) return toast.error('Challan number is required');
    if (!/^\d{7}$/.test(bsrCode.trim())) return toast.error('BSR code must be exactly 7 digits');
    if (!depositDate) return toast.error('Deposit date is required');
    if (!bankName.trim()) return toast.error('Bank name is required');

    setSaving(true);
    try {
      const fyStart = preview.periodMonth >= 4 ? preview.periodYear : preview.periodYear - 1;
      const financialYear = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
      const quarterIdx = Math.floor((((preview.periodMonth - 4 + 12) % 12)) / 3) + 1;
      const quarter = `Q${quarterIdx}`;

      const res = await complianceApi.recordTDSChallanDeposit({
        challanNumber: challanNumber.trim(),
        bsrCode: bsrCode.trim(),
        bankName: bankName.trim(),
        depositDate,
        quarter,
        financialYear,
        entryIds: preview.entryIds,
        totalAmount: preview.totalTDS,
        interestAmount: preview.interest,
        totalPayable: preview.totalPayable,
        periodMonth: preview.periodMonth,
        periodYear: preview.periodYear,
        remarks: remarks.trim() || undefined,
        challanType: type,
      });
      if (res?.success) {
        toast.success(`Challan ${challanNumber.trim()} recorded — ${typeLabel} deposited for ${MONTH_NAMES[preview.periodMonth - 1]} ${preview.periodYear}`);
        onGenerated();
      } else {
        toast.error(res?.message || 'Failed to record deposit');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to record deposit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">
              {step === 'select' ? `Generate ${typeLabel} Challan Details` : step === 'preview' ? `Challan ITNS 281 — ${MONTH_NAMES[preview!.periodMonth - 1]} ${preview!.periodYear}` : `Mark ${typeLabel} as Deposited`}
            </h3>
            <p className="text-xs text-slate-400">
              {step === 'select' ? `Select the month to aggregate un-deposited ${type === 'TCS' ? 'buyer TCS' : 'vendor TDS'}` : step === 'preview' ? `${typeLabel} Payment Details` : `${MONTH_NAMES[preview!.periodMonth - 1]} ${preview!.periodYear}`}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 'select' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Month</label>
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]">
                    {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Year</label>
                  <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]">
                    {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                {type === 'TCS'
                  ? 'Aggregates all TCS collected from buyers (Section 206C) this month that hasn\'t been linked to a challan yet.'
                  : 'Aggregates all vendor TDS (Section 194-series) deducted this month that hasn\'t been linked to a challan yet.'}
              </p>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2.5 bg-slate-50 border border-[#e2e8f0] rounded-lg p-3 text-xs">
                <div><p className="text-slate-400">Company TAN</p><p className="font-semibold text-[#1e293b] font-mono">{preview.companyTan || 'Not set — see TDS/TCS Settings'}</p></div>
                <div><p className="text-slate-400">Company PAN</p><p className="font-semibold text-[#1e293b] font-mono">{preview.companyPan || '—'}</p></div>
                <div><p className="text-slate-400">Assessment Year</p><p className="font-semibold text-[#1e293b]">{preview.assessmentYear}</p></div>
                <div><p className="text-slate-400">Payment Period</p><p className="font-semibold text-[#1e293b]">{MONTH_NAMES[preview.periodMonth - 1]} {preview.periodYear}</p></div>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Section-wise Breakup</p>
                <div className="border border-[#e2e8f0] rounded-lg divide-y divide-[#f1f5f9]">
                  {preview.sectionWise.map((s) => (
                    <div key={s.section} className="px-3 py-2 flex items-center justify-between">
                      <span className="font-medium text-[#1e293b]">Section {s.section}</span>
                      <span className="text-right">
                        <span className="block text-xs text-slate-400">Taxable {formatINR(s.totalTaxable)}</span>
                        <span className="block font-semibold text-[#1e293b]">{typeLabel} {formatINR(s.totalTDS)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                <div className="flex justify-between px-3 py-2 border-b border-[#f1f5f9]"><span className="text-slate-500">{typeLabel} Amount</span><span className="font-semibold text-[#1e293b]">{formatINR(preview.totalTDS)}</span></div>
                {preview.interest > 0 && (
                  <div className="flex justify-between px-3 py-2 border-b border-[#f1f5f9] text-red-600">
                    <span>Interest (Late)</span><span className="font-semibold">{formatINR(preview.interest)} (1.5% × {preview.monthsLate} mo)</span>
                  </div>
                )}
                <div className="flex justify-between px-3 py-2.5 bg-[#4f46e5] text-white font-bold"><span>Total Payable</span><span>{formatINR(preview.totalPayable)}</span></div>
              </div>

              <div className={`rounded-lg p-3 border text-xs ${preview.isLate ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                {preview.isLate ? (
                  <><i className="ri-error-warning-line mr-1" />OVERDUE: {preview.monthsLate} month{preview.monthsLate === 1 ? '' : 's'} late (Due: {formatDate(preview.dueDate)}, Today: {formatDate(new Date())})</>
                ) : (
                  <><i className="ri-checkbox-circle-line mr-1" />On time — due {formatDate(preview.dueDate)}</>
                )}
              </div>

              <div className="bg-slate-50 border border-[#e2e8f0] rounded-lg p-3 text-xs text-slate-600 space-y-1">
                <p className="font-bold text-slate-500 uppercase tracking-wide mb-1.5">How to Pay</p>
                <p>1. Go to: onlineservices.tin.egov-nsdl.com</p>
                <p>2. Select: Challan ITNS 281</p>
                <p>3. Enter TAN: {preview.companyTan || '(set in TDS/TCS Settings)'}</p>
                <p>4. Select: Company Deductees</p>
                <p>5. Select: Section {preview.sectionWise.map((s) => s.section).join(' and ')}</p>
                <p>6. Amount: {formatINR(preview.totalPayable)}</p>
                <p>7. Pay online or at bank</p>
                <p>8. Note challan number, BSR code, date</p>
                <p>9. Come back and click &quot;Mark as Deposited&quot;</p>
              </div>
            </div>
          )}

          {step === 'deposit' && preview && (
            <div className="space-y-4">
              <div className="border border-[#e2e8f0] rounded-lg overflow-hidden text-sm">
                <div className="flex justify-between px-3 py-2 border-b border-[#f1f5f9]"><span className="text-slate-500">{typeLabel} Amount</span><span className="font-medium text-[#1e293b]">{formatINR(preview.totalTDS)}</span></div>
                <div className="flex justify-between px-3 py-2 border-b border-[#f1f5f9]"><span className="text-slate-500">Interest Paid</span><span className="font-medium text-[#1e293b]">{formatINR(preview.interest)}</span></div>
                <div className="flex justify-between px-3 py-2 bg-slate-50"><span className="font-semibold text-[#1e293b]">Total Deposited</span><span className="font-bold text-[#4f46e5]">{formatINR(preview.totalPayable)}</span></div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Challan Number <span className="text-red-500">*</span></label>
                <input type="text" value={challanNumber} onChange={(e) => setChallanNumber(e.target.value)}
                  placeholder="From payment receipt / bank counterfoil"
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">BSR Code <span className="text-red-500">*</span></label>
                  <input type="text" value={bsrCode} onChange={(e) => setBsrCode(e.target.value.replace(/\D/g, '').slice(0, 7))}
                    placeholder="7-digit bank branch code" maxLength={7}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm font-mono focus:outline-none focus:border-[#4f46e5]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Deposit Date <span className="text-red-500">*</span></label>
                  <input type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)} max={new Date().toISOString().split('T')[0]}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Bank Name <span className="text-red-500">*</span></label>
                <input type="text" value={bankName} onChange={(e) => setBankName(e.target.value)}
                  placeholder="e.g. HDFC Bank"
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Deposited By</label>
                <input type="text" value={user?.name || ''} readOnly
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-slate-50 text-slate-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Remarks</label>
                <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g. Deposited via online IT portal"
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]" />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-[#e2e8f0] shrink-0">
          <div>
            {step === 'preview' && (
              <button onClick={handleCopy} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
                <i className="ri-file-copy-line" /> Copy Challan Details
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step !== 'select' && (
              <button
                onClick={() => setStep(step === 'deposit' ? 'preview' : 'select')}
                className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
              >
                ← Back
              </button>
            )}
            <button onClick={onClose} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
              Cancel
            </button>
            {step === 'select' && (
              <button onClick={handleGeneratePreview} disabled={loading}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-file-list-3-line" />} Generate
              </button>
            )}
            {step === 'preview' && (
              <button onClick={() => setStep('deposit')}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap">
                <i className="ri-check-line" /> Mark as Deposited
              </button>
            )}
            {step === 'deposit' && (
              <button onClick={handleConfirmDeposit} disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-checkbox-circle-line" />} Confirm Deposit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
